import { ApplicationRef, inject, Injectable, NgZone } from '@angular/core'
import { BehaviorSubject, delay, EmptyError, interval, Observable, Subscription } from 'rxjs'
import { filter, first, mergeMap, switchMap, tap } from 'rxjs/operators'

import { MessageService } from 'primeng/api'
import { get } from 'lodash-es'

import { TranslatedLongRunningJob } from '@app/components/long-running-jobs/long-running-job.utils'
import { newUser, SessionUser } from '@app/components/roles/user.utils'
import { LongRunningJobsService } from '@app/generated/api/long-running-jobs/long-running-jobs.service'
import { LongRunningJob } from '@app/generated/api/model/longRunningJob'
import { LongRunningJobStatus } from '@app/generated/api/model/longRunningJobStatus'

import { UserService } from './user.service'

@Injectable({ providedIn: 'root' })
export class LongRunningJobService {
  private readonly messageService = inject(MessageService)
  private readonly zone = inject(NgZone)
  private readonly longRunningJobsService = inject(LongRunningJobsService)
  private readonly userService = inject(UserService)
  private readonly ngZone = inject(NgZone)
  public readonly appRef = inject(ApplicationRef)

  private _jobs: TranslatedLongRunningJob[] = []
  private jobsList = new BehaviorSubject<TranslatedLongRunningJob[]>([])

  private _broadcastMessage = new BehaviorSubject<string>('')
  broadcastMessage$ = this._broadcastMessage.asObservable()

  private user: SessionUser = newUser()
  public userSubscription!: Subscription

  constructor () {
    // listen for the user to login, and then launch the LRJ fetch / poll machine
    this.userSubscription = this.userService.getUser()
      .pipe(
        tap(user => {
          this.user = user // Set user in service.
        }),
        filter(user => !user.anonymous), // Filter out anonymous users.
        filter(_ => this.userService.hasPermissionIn(['view_longrunningjob'])), // Filter out users without proper permission.
        mergeMap(_ => this.appRef.isStable), // Listen to app status
        first(stable => stable), // Wait for the first time the app is stable
        // app is stable, user is logged in, fetch their existing Long Running Jobs to initialise our list.
        delay(3000), // we delay this by a few seconds because it is not priority, and we allow other calls to pass first
        tap(() => this.resetLongRunningJobs()),
        // launch poll checker every 2 seconds which will poll individual LRJs once they are started
        switchMap(() => interval(2000)),
      ).subscribe({
        next: () => this.ngZone.run(() => this.pollJobs()),
        // we need to add an empty error handler because first() throws an `EmptyError` in the tests
        // when the emitter is killed before the first value is processed
        // https://rxjs.dev/api/operators/first
        error: (error) => {
          if (error instanceof EmptyError) return
          throw error
        },
      })
  }

  /**
   * Broadcast a message with payload job_type:job_status to potential consumers for this job
   */
  broadCastMessage (job: TranslatedLongRunningJob) {
    // perform this in zone to ensure that if there is a table to refresh the new data is rendered.
    this.zone.run(() => {
      this._broadcastMessage.next(`${job.type}:${job.status}`)
    })
  }

  /**
   * Reset the current list of LRJs and re-fetch the total list from the server.
   */
  resetLongRunningJobs () {
    if (this.user.anonymous) return

    this.longRunningJobsService.list({}).subscribe(response => {
      this._jobs = []
      response.results.forEach(result => {
        this.addJob(result)
      })
      this.jobsList.next(this._jobs)
    })
  }

  /**
   * Poll a specific job to track its progression.
   *
   * Broadcasts a message when done.
   */
  pollJob (job: TranslatedLongRunningJob) {
    this.longRunningJobsService.retrieve(job.code).subscribe(response => {
      // LongRunningJob's fields are readonly (server-controlled) in the generated type,
      // so build a new job object rather than mutating the one already in `_jobs`.
      const updatedJob: TranslatedLongRunningJob = {
        ...job,
        progress: response.progress,
        status: response.status,
        message: response.message as Record<string, any> | null,
      }
      // perform this in zone to ensure the updated data is rendered
      this.zone.run(() => {
        const index = this._jobs.indexOf(job)
        if (index !== -1) this._jobs[index] = updatedJob
        this.jobsList.next([...this._jobs])
        if (updatedJob.message && typeof updatedJob.message === 'object' && 'warning' in updatedJob.message) {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: (updatedJob.message as Record<string, string>).warning, sticky: true })
        }
      })
      // if the job was success, broadcast the message to maybe trigger a table refresh, for example (e.g. crm list)
      if (updatedJob.status === LongRunningJobStatus.DONE) {
        this.broadCastMessage(updatedJob)
      }
    })
  }

  /**
   * Return the translated human-readable string for a LRJ type
   */
  getTranslatedTypeName (typeCode: string) {
    // Define all possible long running job types here.
    // They will be automatically translatable via angular i18n.
    // TODO: this list should be added to a global constants page probably.
    const typeCodes: { [code: string]: string } = {
      import_assets: $localize`:@@long-running-jobs-import-assets:Import assets`,
      import_complaints: $localize`:@@long-running-jobs-import-complaints:Import complaints`,
      import_financial_sheet_version: $localize`:@@long-running-jobs-import-financial-sheet-version:Import financial sheet`,
      import_procurement_plan: $localize`:@@long-running-jobs-import-procurement-plan:Import procurement plan`,
      import_staff: $localize`:@@long-running-jobs-import-staff:Import staff`,
      import_staff_lite: $localize`:@@long-running-jobs-import-staff:Import staff`,
      import_screening_staff: $localize`:@@long-running-jobs-import-screening-staff:Import staff for screening`,
      export_archive_documents: $localize`:@@long-running-jobs-export-archive-documents:Export documents`,
      export_assets: $localize`:@@long-running-jobs-export-assets:Export assets`,
      export_asset_inventory: $localize`:@@long-running-jobs-export-asset-inventory:Export asset inventory`,
      export_asset_inventories: $localize`:@@long-running-jobs-export-asset-inventories:Export asset inventories`,
      export_asset_usages: $localize`:@@long-running-jobs-export-asset-usages:Export asset assignments`,
      export_balzac_books: $localize`:@@long-running-jobs-export-balzac-books:Export balzac books`,
      export_budget_lines: $localize`:@@long-running-jobs-export-budget-lines:Export budget lines`,
      export_certifications: $localize`:@@long-running-jobs-export-certifications:Export donor eligibilities`,
      export_complaints: $localize`:@@long-running-jobs-export-complaints:Export complaints`,
      export_countries: $localize`:@@long-running-jobs-export-countries:Export countries`,
      export_deliverables: $localize`:@@long-running-jobs-export-deliverables:Export deliverables`,
      export_donors: $localize`:@@long-running-jobs-export-donors:Export donors`,
      export_external_audits: $localize`:@@long-running-jobs-export-external-audits:Export external audits`,
      export_grasp_entries: $localize`:@@long-running-jobs-export-grasp-entries:Export GRASP`,
      export_incidents: $localize`:@@long-running-jobs-export-incidents:Export incidents`,
      export_mitigation_plan: $localize`:@@long-running-jobs-export_mitigation_plan:Export mitigation plan`,
      export_phone_lines: $localize`:@@long-running-jobs-export-phone-lines:Export phone lines`,
      export_premises: $localize`:@@long-running-jobs-export-premises:Export premises`,
      export_projects: $localize`:@@long-running-jobs-export-projects:Export projects`,
      export_partners: $localize`:@@long-running-jobs-export-partners:Export partners`,
      export_screening_entities: $localize`:@@long-running-jobs-export-screening-entities:Export screening entities`,
      export_staff_impact: $localize`:@@long-running-jobs-export-staff_impact:Export IMPACT staff`,
      export_suppliers: $localize`:@@long-running-jobs-export-suppliers:Export suppliers`,
      validate_screening_entities: $localize`:@@long-running-jobs-validate_screening_entities:Validate screening entities`,
      mark_certifications_updated: $localize`:@@long-running-jobs-mark_certifications_updated:Marking certifications as updated`,
      saga_double_accounting: $localize`:@@long-running-jobs-saga_double_accounting:SAGA double accounting`,
    }

    // Return the translated version, falling back to the type code if it wasn't supplied above.
    return get(typeCodes, typeCode, typeCode)
  }

  /**
   * Add a new job to the user's current list, presumably following its creation.
   */
  addJob (job: LongRunningJob) {
    const translatedJob: TranslatedLongRunningJob = {
      ...job,
      message: job.message as Record<string, any> | null,
      translatedTypeName: this.getTranslatedTypeName(job.type),
    }
    this._jobs.push(translatedJob)
    this.jobsList.next([...this._jobs])
  }

  /**
   * Remove a job from the global list, probably coz the user clicked the 'close' button.
   */
  removeJob (job: TranslatedLongRunningJob) {
    this._jobs.splice(this._jobs.indexOf(job), 1)
    this.jobsList.next(this._jobs)
  }

  getJobs (): Observable<TranslatedLongRunningJob[]> {
    return this.jobsList.asObservable()
  }

  /**
   * loop over all running jobs and call their individual poll.
   */
  private pollJobs () {
    this._jobs
      .filter(job => ([LongRunningJobStatus.WAITING, LongRunningJobStatus.RUNNING] as LongRunningJobStatus[]).includes(job.status))
      .forEach(job => this.pollJob(job))
  }
}
